/* eslint-disable indent */
import { useState } from 'react';
import { postApi } from '../utils/api';
import { IConsultationOutcome } from './promises/getConsultationOutcomes';
import { IConsultationType } from './promises/getConsultationTypes';
import { IFollowUpMethod } from './promises/getFollowUpMethods';
import { IFollowUpReason } from './promises/getFollowUpReasons';
import { IGeneralPractice } from './promises/getGeneralPractices';
import { IModeOfContact } from './promises/getModeOfContacts';
import { IReferralSource } from './promises/getReferralSources';
import { IPatientAgeRange } from './promises/getPatientAgeRanges';
import { IDoubleAppointmentReason } from './promises/getDoubleAppointmentReasons';
import { consultationTypeRequiresFollowUp } from '../pages/RegisterAppointment/helpers';

export interface ICreateAppointmentRequestValidationResult {
  validationMessages: string[];
  success: boolean;
}

export type StateSetter<T> = React.Dispatch<React.SetStateAction<T>>;

interface IAppointmentConsultationOutcomeRequest {
  consultationOutcomeId: number;
  subOutcomeId?: number;
  otherText?: string;
}

interface ICreateAppointmentRequest {
  organisationId: number;
  consultationDate: string;
  referralSourceId: number;
  modeOfContactId: number;
  patientAgeRangeId: number;
  doubleAppointmentReasonId: number;
  followUpMethodId: number;
  followUpReasonId: number;
  consultationTypeId: number;
  doubleAppointmentFlag: boolean;
  didNotAttendFlag: boolean;
  deletedFlag: boolean;
  lateCancellationFlag: boolean;
  interpreterRequiredFlag: boolean;
  appointmentConsultationOutcomeList: IAppointmentConsultationOutcomeRequest[];
}

export interface ICreateAppointmentBuilder {
  setDateOfConsultation: StateSetter<Date | null>;
  setFcpName: StateSetter<string>;
  setOrganisation: StateSetter<IGeneralPractice | null>;
  setPcnName: StateSetter<string>;
  setIcsCcgName: StateSetter<string>;
  setReferralSource: StateSetter<IReferralSource | null>;
  setModeOfContact: StateSetter<IModeOfContact | null>;
  setConsultationType: StateSetter<IConsultationType | null>;
  setConsultationOutcomes: StateSetter<IConsultationOutcome[]>;
  setCustomConsultationOutcome: StateSetter<string>;
  setDidNotAttend: StateSetter<boolean>;
  setDoubleAppointment: StateSetter<boolean>;
  setLateCancellation: StateSetter<boolean>;
  setInterpreterRequired: StateSetter<boolean>;
  setFollowUpMethod: StateSetter<IFollowUpMethod | null>;
  setFollowUpReason: StateSetter<IFollowUpReason | null>;
  setPatientAgeRange: StateSetter<IPatientAgeRange | null>;
  setDoubleAppointmentReason: StateSetter<IDoubleAppointmentReason | null>;
  dateOfConsultation: Date | null;
  fcpName: string;
  organisation: IGeneralPractice | null;
  pcnName: string;
  icsCcgName: string;
  referralSource: IReferralSource | null;
  modeOfContact: IModeOfContact | null;
  consultationType: IConsultationType | null;
  consultationOutcomes: IConsultationOutcome[];
  customConsultationOutcome: string;
  didNotAttend: boolean;
  doubleAppointment: boolean;
  lateCancellation: boolean;
  interpreterRequired: boolean;
  followUpMethod: IFollowUpMethod | null;
  followUpReason: IFollowUpReason | null;
  patientAgeRange: IPatientAgeRange | null;
  doubleAppointmentReason: IDoubleAppointmentReason | null;
}

export function useCreateAppointmentRequest() {
  const [dateOfConsultation, setDateOfConsultation] = useState<Date | null>(
    new Date()
  );
  const [fcpName, setFcpName] = useState('');
  const [organisation, setOrganisation] = useState<IGeneralPractice | null>(
    null
  );
  const [pcnName, setPcnName] = useState('');
  const [icsCcgName, setIcsCcgName] = useState('');
  const [referralSource, setReferralSource] = useState<IReferralSource | null>(
    null
  );
  const [modeOfContact, setModeOfContact] = useState<IModeOfContact | null>(
    null
  );
  const [consultationType, setConsultationType] =
    useState<IConsultationType | null>(null);
  const [followUpMethod, setFollowUpMethod] = useState<IFollowUpMethod | null>(
    null
  );
  const [followUpReason, setFollowUpReason] = useState<IFollowUpReason | null>(
    null
  );
  const [didNotAttend, setDidNotAttend] = useState(false);
  const [doubleAppointment, setDoubleAppointment] = useState(false);
  const [lateCancellation, setLateCancellation] = useState(false);
  const [interpreterRequired, setInterpreterRequired] = useState(false);
  const [consultationOutcomes, setConsultationOutcomes] = useState<
    IConsultationOutcome[]
  >([]);
  const [customConsultationOutcome, setCustomConsultationOutcome] =
    useState<string>('');
  const [patientAgeRange, setPatientAgeRange] =
    useState<IPatientAgeRange | null>(null);
  const [doubleAppointmentReason, setDoubleAppointmentReason] =
    useState<IDoubleAppointmentReason | null>(null);
  const builder: ICreateAppointmentBuilder = {
    setDateOfConsultation,
    setFcpName,
    setOrganisation,
    setPcnName,
    setIcsCcgName,
    setReferralSource,
    setModeOfContact,
    setConsultationType,
    setConsultationOutcomes,
    setCustomConsultationOutcome,
    setDidNotAttend,
    setDoubleAppointment,
    setLateCancellation,
    setInterpreterRequired,
    setFollowUpMethod,
    setFollowUpReason,
    setPatientAgeRange,
    setDoubleAppointmentReason,
    dateOfConsultation,
    fcpName,
    organisation,
    pcnName,
    icsCcgName,
    referralSource,
    modeOfContact,
    consultationType,
    consultationOutcomes,
    customConsultationOutcome,
    didNotAttend,
    doubleAppointment,
    lateCancellation,
    interpreterRequired,
    followUpMethod,
    followUpReason,
    patientAgeRange,
    doubleAppointmentReason,
  };

  const validateRequest = (): ICreateAppointmentRequestValidationResult => {
    const validationErrors: string[] = [];

    const addError = (name: string) =>
      validationErrors.push(`${name} must be provided.`);

    if (dateOfConsultation === null) addError('A date of consultation');
    if (!fcpName) addError('An FCP name');
    if (!organisation) addError('A GP practice');
    if (!pcnName) addError('A PCN name');
    if (!icsCcgName) addError('An ICB name');
    if (!referralSource) addError('A referral source');
    if (!modeOfContact) addError('A mode of contact');
    if (!patientAgeRange) addError('A patient age range');
    if (!consultationType) addError('A consultation type');
    if (consultationOutcomes.length === 0 && !didNotAttend && !lateCancellation)
      addError('A consultation outcome');
    if (
      consultationOutcomes.filter((x) => x.consultationOutcomeId === 24)
        .length > 0 &&
      !customConsultationOutcome
    )
      addError('If custom outcome is checked, a reason');

    const hasOutcomesWithSubOutcomeAndNoSelectedOutcome =
      consultationOutcomes.filter(
        (x) => x.subOutcomes.length > 0 && !x.selectedSubOutcome
      ).length > 0;
    if (hasOutcomesWithSubOutcomeAndNoSelectedOutcome)
      addError('A sub outcome');

    if (
      consultationType &&
      consultationTypeRequiresFollowUp(consultationType.consultationTypeId) &&
      !followUpMethod
    )
      addError('A follow up method');
    if (
      consultationType &&
      consultationTypeRequiresFollowUp(consultationType.consultationTypeId) &&
      !followUpReason
    )
      addError('A follow up reason');
    if (doubleAppointment && !doubleAppointmentReason)
      addError('Double appointment reason');

    return {
      success: validationErrors.length === 0,
      validationMessages: validationErrors,
    };
  };

  const createRequest = (): ICreateAppointmentRequest | null => {
    if (dateOfConsultation === null) return null;
    if (!organisation) return null;
    if (!fcpName) return null;
    if (!pcnName) return null;
    if (!icsCcgName) return null;
    if (!referralSource) return null;
    if (!modeOfContact) return null;
    if (!consultationType) return null;
    if (!patientAgeRange) return null;

    let followUpMethodId = 0;
    let followUpReasonId = 0;

    if (consultationTypeRequiresFollowUp(consultationType.consultationTypeId)) {
      if (!followUpMethod) return null;
      if (!followUpReason) return null;
      followUpMethodId = followUpMethod.followUpMethodId;
      followUpReasonId = followUpReason.followUpReasonId;
    }

    const date = new Date(dateOfConsultation);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const formattedDate = new Date(Date.UTC(year, month, day, 0, 0, 0));

    return {
      organisationId: organisation.organisationId,
      consultationDate: formattedDate.toISOString(),
      referralSourceId: referralSource.referralSourceId,
      modeOfContactId: modeOfContact.modeOfContactId,
      patientAgeRangeId: patientAgeRange.patientAgeRangeId,
      consultationTypeId: consultationType.consultationTypeId,
      doubleAppointmentFlag: doubleAppointment,
      deletedFlag: false,
      didNotAttendFlag: didNotAttend,
      lateCancellationFlag: lateCancellation,
      interpreterRequiredFlag: interpreterRequired,
      doubleAppointmentReasonId:
        doubleAppointmentReason?.doubleAppointmentReasonId || 0,
      appointmentConsultationOutcomeList: didNotAttend
        ? []
        : consultationOutcomes.map((x) => ({
            consultationOutcomeId: x.consultationOutcomeId,
            otherText:
              x.consultationOutcomeId ===
              Number(process.env.REACT_APP_OUTCOME_OTHER_BOX_ID)
                ? customConsultationOutcome
                : '',
            subOutcomeId: x.selectedSubOutcome?.consultationSubOutcomeId,
          })),
      followUpMethodId,
      followUpReasonId,
    };
  };

  function createAppointment(request: ICreateAppointmentRequest) {
    return postApi<unknown, ICreateAppointmentRequest>('Appointments', request);
  }

  return { builder, validateRequest, createRequest, createAppointment };
}
